import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDateiDrop } from '../lib/useDateiDrop';
import './StuecklistenImport.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const ACCEPT = '.ugl,.csv,.xlsx,.xls';

interface Props { authToken: string; }

type Schritt = 'upload' | 'vorschau' | 'ergebnis';

interface MatchEintrag {
  pos: number;
  rohdaten: {
    menge: number; einheit: string; lieferanten_artikelnummer: string;
    ean: string; bezeichnung: string; einkaufspreis: number | null; hersteller: string;
  };
  match_status: 'GEFUNDEN' | 'VORSCHLAG' | 'NEU';
  artikel_id: number | null;
  vorschlaege: { artikel_id: number; bezeichnung: string }[];
}

interface AnalyseResponse {
  import_id: number; format: string; dateiname: string; positionen: MatchEintrag[];
}

interface UebernahmeErgebnis {
  verkaufsartikel_id: number; angelegt: number; verknuepft: number; uebersprungen: number;
}

type PositionsAktion = 'verwenden' | 'neu' | 'ueberspringen';

const AMPEL_LABEL: Record<MatchEintrag['match_status'], string> = {
  GEFUNDEN: 'Gefunden',
  VORSCHLAG: 'Vorschlag',
  NEU: 'Neu',
};

const AMPEL_KLASSE: Record<MatchEintrag['match_status'], string> = {
  GEFUNDEN: 'stkl-ampel--gefunden',
  VORSCHLAG: 'stkl-ampel--vorschlag',
  NEU: 'stkl-ampel--neu',
};

const StuecklistenImport: React.FC<Props> = ({ authToken }) => {
  const [schritt, setSchritt] = useState<Schritt>('upload');
  const [datei, setDatei] = useState<File | null>(null);
  const [partnerId, setPartnerId] = useState<string>('');
  const [lieferanten, setLieferanten] = useState<{ partner_id: number; name: string }[]>([]);
  const [analyse, setAnalyse] = useState<AnalyseResponse | null>(null);
  const [aktionen, setAktionen] = useState<Record<number, { aktion: PositionsAktion; artikel_id?: number }>>({});
  const [vaBezeichnung, setVaBezeichnung] = useState('');
  const [ekUebernehmen, setEkUebernehmen] = useState(false);
  const [vkAbleiten, setVkAbleiten] = useState(false);
  const [ergebnis, setErgebnis] = useState<UebernahmeErgebnis | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const headers = { Authorization: `Bearer ${authToken}` };
  const { dragOver, dropHandlers } = useDateiDrop({
    accept: ACCEPT,
    onFiles: (dateien) => setDatei(dateien[0]),
    onError: (fehler) => setErrorMsg(fehler),
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/partners?typ=Lieferant`, { headers })
      .then((r) => setLieferanten(r.data))
      .catch(() => {
        setLieferanten([]);
        setErrorMsg('Lieferantenliste konnte nicht geladen werden.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analysieren = async () => {
    if (!datei) { setErrorMsg('Bitte eine Datei wählen (.ugl, .csv, .xlsx).'); return; }
    setLoading(true); setErrorMsg(null);
    try {
      const fd = new FormData();
      fd.append('datei', datei);
      const q = partnerId ? `?partner_id=${partnerId}` : '';
      const r = await axios.post<AnalyseResponse>(
        `${API_URL}/api/import/stueckliste/analyse${q}`, fd, { headers });
      setAnalyse(r.data);
      const initial: Record<number, { aktion: PositionsAktion; artikel_id?: number }> = {};
      r.data.positionen.forEach((p) => {
        initial[p.pos] = p.match_status === 'GEFUNDEN'
          ? { aktion: 'verwenden', artikel_id: p.artikel_id ?? undefined }
          : p.match_status === 'NEU' ? { aktion: 'neu' } : { aktion: 'ueberspringen' };
      });
      setAktionen(initial);
      setSchritt('vorschau');
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? 'Analyse fehlgeschlagen.');
    } finally { setLoading(false); }
  };

  const uebernehmen = async () => {
    if (!analyse) return;
    if (!vaBezeichnung.trim()) { setErrorMsg('Bitte eine Bezeichnung für den Verkaufsartikel angeben.'); return; }
    setLoading(true); setErrorMsg(null);
    try {
      const body = {
        verkaufsartikel_bezeichnung: vaBezeichnung.trim(),
        ek_uebernehmen: ekUebernehmen,
        vk_ableiten: ekUebernehmen && vkAbleiten,
        positionen: Object.entries(aktionen).map(([pos, a]) => ({
          pos: Number(pos), aktion: a.aktion, artikel_id: a.artikel_id ?? null,
        })),
      };
      const r = await axios.post<UebernahmeErgebnis>(
        `${API_URL}/api/import/stueckliste/${analyse.import_id}/uebernehmen`, body, { headers });
      setErgebnis(r.data);
      setSchritt('ergebnis');
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? 'Übernahme fehlgeschlagen.');
    } finally { setLoading(false); }
  };

  const setAktion = (pos: number, patch: Partial<{ aktion: PositionsAktion; artikel_id?: number }>) => {
    setAktionen((prev) => ({ ...prev, [pos]: { ...prev[pos], ...patch } }));
  };

  const handleNeustart = () => {
    setSchritt('upload');
    setDatei(null);
    setAnalyse(null);
    setAktionen({});
    setVaBezeichnung('');
    setEkUebernehmen(false);
    setVkAbleiten(false);
    setErgebnis(null);
    setErrorMsg(null);
  };

  return (
    <div className="stkl-import">
      <div className="stkl-import__header">
        <h2>Stücklisten-Import</h2>
        <div className="stkl-steps">
          <span className={`stkl-step ${schritt === 'upload' ? 'stkl-step--active' : 'stkl-step--done'}`}>
            1 Datei
          </span>
          <span className="stkl-step__sep">›</span>
          <span className={`stkl-step ${schritt === 'vorschau' ? 'stkl-step--active' : schritt === 'ergebnis' ? 'stkl-step--done' : ''}`}>
            2 Vorschau
          </span>
          <span className="stkl-step__sep">›</span>
          <span className={`stkl-step ${schritt === 'ergebnis' ? 'stkl-step--active' : ''}`}>
            3 Ergebnis
          </span>
        </div>
      </div>

      <div className="stkl-import__info">
        Importiert eine einzelne Stückliste bzw. einen Warenkorb-Export des Großhändlers (UGL, CSV, Excel) und übernimmt sie nach Prüfung als Artikel-Stückliste. Komplette Preiskataloge gehören in den Tab 'Kataloge'.
      </div>

      {errorMsg && <div className="stkl-fehler">{errorMsg}</div>}

      {/* Schritt 1: Upload */}
      {schritt === 'upload' && (
        <div className="stkl-section">
          <div className="stkl-field">
            <label>Lieferant</label>
            <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">— kein Lieferant —</option>
              {lieferanten.map((l) => (
                <option key={l.partner_id} value={l.partner_id}>{l.name}</option>
              ))}
            </select>
          </div>

          <div className="stkl-field">
            <label>Stücklisten-Datei (.ugl, .csv, .xlsx)</label>
            <div
              className={`stkl-dropzone ${dragOver ? 'stkl-dropzone--drag-over' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              {...dropHandlers}
            >
              <span className="stkl-dropzone__text">
                {datei ? datei.name : 'Datei hierher ziehen oder klicken'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={(e) => setDatei(e.target.files?.[0] ?? null)}
              style={{ display: 'none' }}
            />
          </div>

          <button
            className="stkl-btn stkl-btn--primary"
            onClick={analysieren}
            disabled={loading || !datei}
          >
            {loading ? 'Analysiere…' : 'Analysieren'}
          </button>
        </div>
      )}

      {/* Schritt 2: Vorschau + Matching */}
      {schritt === 'vorschau' && analyse && (
        <div className="stkl-section">
          <h3>Positionen prüfen</h3>
          <p className="stkl-hint">
            Prüfe die Zuordnung je Position. Bei „Vorschlag" einen Artikel auswählen oder auf
            „Neu anlegen"/„Überspringen" umstellen.
          </p>

          <div className="stkl-table-wrap">
            <table className="stkl-table">
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Menge</th>
                  <th>Einheit</th>
                  <th>Händler-Nr.</th>
                  <th>EAN</th>
                  <th>Bezeichnung</th>
                  <th>EK</th>
                  <th>Match</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {analyse.positionen.map((p) => (
                  <tr key={p.pos}>
                    <td>{p.pos}</td>
                    <td>{p.rohdaten.menge}</td>
                    <td>{p.rohdaten.einheit}</td>
                    <td>{p.rohdaten.lieferanten_artikelnummer}</td>
                    <td>{p.rohdaten.ean}</td>
                    <td>{p.rohdaten.bezeichnung}</td>
                    <td>{p.rohdaten.einkaufspreis != null ? `${p.rohdaten.einkaufspreis.toFixed(2)} €` : '–'}</td>
                    <td>
                      <span className={`stkl-ampel ${AMPEL_KLASSE[p.match_status]}`}>
                        {AMPEL_LABEL[p.match_status]}
                      </span>
                    </td>
                    <td>
                      <select
                        value={aktionen[p.pos]?.aktion ?? 'ueberspringen'}
                        onChange={(e) => setAktion(p.pos, { aktion: e.target.value as PositionsAktion })}
                      >
                        <option value="verwenden">Verwenden</option>
                        <option value="neu">Neu anlegen</option>
                        <option value="ueberspringen">Überspringen</option>
                      </select>
                      {p.match_status === 'VORSCHLAG' && (
                        <select
                          className="stkl-vorschlag-select"
                          value={aktionen[p.pos]?.artikel_id ?? ''}
                          onChange={(e) => {
                            const wert = e.target.value;
                            if (wert === '') {
                              setAktion(p.pos, { aktion: 'ueberspringen', artikel_id: undefined });
                            } else {
                              setAktion(p.pos, { aktion: 'verwenden', artikel_id: Number(wert) });
                            }
                          }}
                        >
                          <option value="">— Vorschlag wählen —</option>
                          {p.vorschlaege.map((v) => (
                            <option key={v.artikel_id} value={v.artikel_id}>{v.bezeichnung}</option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="stkl-field">
            <label>Verkaufsartikel-Bezeichnung</label>
            <input
              type="text"
              value={vaBezeichnung}
              onChange={(e) => setVaBezeichnung(e.target.value)}
              placeholder="Bezeichnung des neuen Verkaufsartikels"
            />
          </div>

          <label className="stkl-checkbox">
            <input
              type="checkbox"
              checked={ekUebernehmen}
              onChange={(e) => {
                const checked = e.target.checked;
                setEkUebernehmen(checked);
                if (!checked) setVkAbleiten(false);
              }}
            />
            EK-Preise in Artikel übernehmen
          </label>

          <label className="stkl-checkbox" title={ekUebernehmen ? undefined : 'Nur zusammen mit "EK-Preise übernehmen" aktivierbar'}>
            <input
              type="checkbox"
              checked={vkAbleiten}
              disabled={!ekUebernehmen}
              onChange={(e) => setVkAbleiten(e.target.checked)}
            />
            VK-Preis aus Standard-Aufschlag ableiten
          </label>

          <div className="stkl-actions">
            <button className="stkl-btn" onClick={() => setSchritt('upload')}>
              Zurück
            </button>
            <button
              className="stkl-btn stkl-btn--primary"
              onClick={uebernehmen}
              disabled={loading}
            >
              {loading ? 'Lege an…' : 'Stückliste anlegen'}
            </button>
          </div>
        </div>
      )}

      {/* Schritt 3: Ergebnis */}
      {schritt === 'ergebnis' && ergebnis && (
        <div className="stkl-section">
          <h3>Import abgeschlossen</h3>

          <div className="stkl-stats">
            <div className="stkl-stat stkl-stat--ok">
              <span className="stkl-stat__zahl">{ergebnis.angelegt}</span>
              <span className="stkl-stat__label">angelegt</span>
            </div>
            <div className="stkl-stat stkl-stat--update">
              <span className="stkl-stat__zahl">{ergebnis.verknuepft}</span>
              <span className="stkl-stat__label">verknüpft</span>
            </div>
            <div className="stkl-stat stkl-stat--err">
              <span className="stkl-stat__zahl">{ergebnis.uebersprungen}</span>
              <span className="stkl-stat__label">übersprungen</span>
            </div>
          </div>

          <p className="stkl-hint">
            Der Verkaufsartikel (ID {ergebnis.verkaufsartikel_id}) ist im Tab „Artikel" zu finden.
          </p>

          <button className="stkl-btn stkl-btn--primary" onClick={handleNeustart}>
            Weiteren Import starten
          </button>
        </div>
      )}
    </div>
  );
};

export default StuecklistenImport;
